import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const checkLoggedIn = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Check if token is expired
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          
          if (decodedToken.exp < currentTime) {
            // Token is expired
            logout();
          } else {
            // Token is valid, get user data
            const userData = await authService.getCurrentUser();
            setCurrentUser(userData);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Authentication error:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setIsLoading(true);
      
      console.log('Attempting login for:', email);
      const { token, user } = await authService.login(email, password);
      
      if (!token) {
        throw new Error('No token received from server');
      }
      
      console.log('Login successful, storing token');
      // Store token in localStorage
      localStorage.setItem('token', token);
      
      // Verify token is stored correctly
      const storedToken = localStorage.getItem('token');
      console.log('Token stored successfully:', !!storedToken);
      
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Navigate based on user role
      const targetPath = user.role === 'admin' ? '/admin/dashboard' : '/dashboard';
      console.log('Navigating to:', targetPath);
      navigate(targetPath);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.msg || error.message || 'Login failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      setIsLoading(true);
      console.log('Attempting to register user:', userData.email);
      
      // Check if we're already authenticated (admin creating a new user)
      if (isAuthenticated && currentUser?.role === 'admin') {
        console.log('Admin creating new user');
        // Just register the user without changing current login state
        await authService.register(userData);
        return true;
      } else {
        console.log('Normal registration flow');
        // Normal registration flow (should not happen with admin-only registration)
        const { token, user } = await authService.register(userData);
        
        if (!token) {
          throw new Error('No token received from server during registration');
        }
        
        console.log('Registration successful, storing token');
        localStorage.setItem('token', token);
        
        // Verify token is stored correctly
        const storedToken = localStorage.getItem('token');
        console.log('Token stored successfully:', !!storedToken);
        
        setCurrentUser(user);
        setIsAuthenticated(true);
        
        // Navigate based on user role
        const targetPath = user.role === 'admin' ? '/admin/dashboard' : '/dashboard';
        console.log('Navigating to:', targetPath);
        navigate(targetPath);
        return true;
      }
    } catch (error) {
      setError(error.response?.data?.msg || 'Registration failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log('Logging out user');
    
    // Remove token from localStorage
    localStorage.removeItem('token');
    
    // Verify token is removed
    const tokenAfterRemoval = localStorage.getItem('token');
    console.log('Token removed successfully:', !tokenAfterRemoval);
    
    // Reset auth state
    setCurrentUser(null);
    setIsAuthenticated(false);
    
    // Redirect to login page
    console.log('Redirecting to login page');
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        isLoading,
        error,
        login,
        register,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthProvider;
